/** `changes` is the default: the first tab and the only one reading both audit tables. */

export type AuditTab = 'changes' | 'person'

export const AUDIT_TABS: readonly AuditTab[] = ['changes', 'person']

export const DEFAULT_AUDIT_TAB: AuditTab = AUDIT_TABS[0]

/** `null` is what `URLSearchParams.get` returns for an absent key, so it is taken directly rather than forcing every caller to coalesce first. */
export function resolveAuditTab(requested: string | null | undefined): AuditTab {
  return AUDIT_TABS.find((tab) => tab === requested) ?? DEFAULT_AUDIT_TAB
}

/** An empty `person` deletes the key rather than writing `?person=`, which would look like a selection and is not. */
export function auditSearch(
  prev: URLSearchParams,
  patch: { tab?: AuditTab; person?: string },
): URLSearchParams {
  const params = new URLSearchParams(prev)
  if (patch.tab !== undefined) params.set('tab', patch.tab)
  if (patch.person !== undefined) {
    if (patch.person) params.set('person', patch.person)
    else params.delete('person')
  }
  return params
}

export const AUDIT_PAGE_SIZE = 50

/** Zero-based; an empty result still has a page 0, so the pager never offers "next" from it. */
export function lastPageIndex(total: number, size: number = AUDIT_PAGE_SIZE): number {
  return Math.max(0, Math.ceil(total / size) - 1)
}

const CHANGE_PAIRS: readonly [string, string][] = [
  ['from', 'to'],
  ['was', 'now'],
  ['old', 'new'],
  ['before', 'after'],
  ['old_amount', 'new_amount'],
]

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function detailValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) {
    if (value.length === 0) return '—'
    return value.map(detailValue).join(value.some(isRecord) ? '\n' : ', ')
  }
  if (isRecord(value)) {
    const keys = Object.keys(value)
    if (keys.length === 0) return '—'
    const pair = CHANGE_PAIRS.find(([from, to]) => from in value && to in value)
    const change = pair ? `${detailValue(value[pair[0]])} → ${detailValue(value[pair[1]])}` : ''
    const rest = keys
      .filter((key) => !pair?.includes(key))
      .map((key) => {
        const inner = detailValue(value[key])
        return isRecord(value[key]) && inner !== '—' ? `${key} (${inner})` : `${key}: ${inner}`
      })
      .join(', ')
    return [rest, change].filter(Boolean).join(', ')
  }
  return String(value)
}

/** One line per key of an entry's free-form detail, for the detail dialog rather than the row. */
export function detailLines(detail: Record<string, unknown> | null): { key: string; value: string }[] {
  if (!detail) return []
  return Object.entries(detail).map(([key, value]) => ({ key, value: detailValue(value) }))
}
